import React, { useState, useRef } from "react";
import adminService from "../services/admin.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const UploadMessagePage = () => {
  const [hour, setHour] = useState(13);
  const [minute, setMinute] = useState(0);
  const [second, setSecond] = useState(0);
  // Get today's date with time set to beginning of day
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dateError, setDateError] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [messageAction, setMessageAction] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [messageFile, setMessageFile] = useState(null);
  const [detailFile, setDetailFile] = useState(null);
  const [repeatIntervalDays, setRepeatIntervalDays] = useState("");
  const [message, setMessage] = useState("");
  const [normalMessageFile, setNormalMessageFile] = useState(null);
  const [successful, setSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messageUrl, setMessageUrl] = useState("");
  const [detailUrl, setDetailUrl] = useState("");
  const [normalMessageUrl, setNormalMessageUrl] = useState("");

  const messageFileRef = useRef();
  const detailFileRef = useRef();
  const normalMessageFileRef = useRef();

  // Generate hours options (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Generate minutes/seconds options (0-59)
  const minutesSeconds = Array.from({ length: 60 }, (_, i) => i);

  const handleMessageFileChange = (e) => {
    setMessageFile(e.target.files[0]);
  };

  const handleDetailFileChange = (e) => {
    setDetailFile(e.target.files[0]);
  };

  const handleNormalMessageFileChange = (e) => {
    setNormalMessageFile(e.target.files[0]);
  };

  const formatMessage = (message) => {
    if (message === null || message === undefined) {
      return 'No message provided';
    }

    if (typeof message === 'object') {
      try {
        return JSON.stringify(message);
      } catch (e) {
        return 'Complex error object (could not stringify)';
      }
    }

    return message.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");
    setDateError("");
    setSuccessful(false);
    setIsLoading(true);
    setMessageUrl("");
    setDetailUrl("");
    setNormalMessageUrl("");

    // Validate dates
    if (!startDate || !endDate) {
      setDateError("Both start and end dates are required");
      setIsLoading(false);
      return;
    }

    // Validate start date is today or later
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    if (startDate < todayDate) {
      setDateError("Start date must be today or later");
      setIsLoading(false);
      return;
    }

    // Validate end date is equal to or later than start date
    if (endDate < startDate) {
      setDateError("End date must be equal to or later than start date");
      setIsLoading(false);
      return;
    }

    if (!messageFile) {
      setMessage("Please select a message page file");
      setSuccessful(false);
      setIsLoading(false);
      return;
    }

    try {
      const response = await adminService.uploadMessagePage(hour, minute, second, messageTitle, messageAction, targetUrl, messageFile, detailFile, startDate, endDate, repeatIntervalDays, normalMessageFile);

      // Check if response has the expected URLs
      if (response.data && response.data.messageUrl) {
        const { messageUrl: msgUrl, detailUrl: dtlUrl, normalMessageUrl: normalUrl } = response.data;

        // Store URLs in state
        setMessageUrl(msgUrl);
        setDetailUrl(dtlUrl || '');
        setNormalMessageUrl(normalUrl || '');

        // Open windows side by side
        // openWindowsSideBySide(msgUrl, dtlUrl);

        // Set success message
        setMessage("Files uploaded successfully!");
        setSuccessful(true);

        // Clear form
        setMessageTitle("");
        setTargetUrl("");
        setRepeatIntervalDays("");
        setNormalMessageFile(null);
        setMessageFile(null);
        setDetailFile(null);
        if (messageFileRef.current) messageFileRef.current.value = "";
        if (detailFileRef.current) detailFileRef.current.value = "";
        if (normalMessageFileRef.current) normalMessageFileRef.current.value = "";

      } else {
        // Handle unexpected response format
        setMessage("Upload successful but no URLs were returned");
        setSuccessful(false);
        toast.warning('Upload successful but received unexpected data format');
      }
    } catch (error) {
      console.error("Error uploading message page:", error);
      let resMessage = "An error occurred while uploading the message page";

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 404) {
          resMessage = "The service endpoint is not available (404). The server side may not be ready yet.";
        } else if (error.response.data) {
          const errorMessage = error.response.data.message ? formatMessage(error.response.data.message) : 'Internal Server Error';
          const errorType = error.response.data.error ? formatMessage(error.response.data.error) : 'Unknown Error';
          resMessage = `${errorMessage} (${errorType})`;
        }
      } else if (error.message) {
        resMessage = error.message;
      }

      setMessage(resMessage);
      setSuccessful(false);
      toast.error(typeof error.message === 'string' ? error.message : 'Error uploading files', {
        position: "top-center",
        autoClose: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid py-5" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
      <div className="card border-0 shadow-lg mx-auto" style={{ borderRadius: '24px', maxWidth: '800px', overflow: 'hidden' }}>
        <div className="card-header bg-white border-0 py-4 px-4 px-md-5 d-flex align-items-center">
          <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle me-3" style={{ width: '48px', height: '48px' }}>
            <i className="bi bi-cloud-arrow-up text-primary fs-3"></i>
          </div>
          <h3 className="fw-bold mb-0 text-dark">Upload Message Page</h3>
        </div>
        <div className="card-body p-4 p-md-5 pt-0">
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-2">Schedule Open Message Window at:</label>
              <div className="d-flex gap-3">
                <div className="flex-fill">
                  <label className="text-muted small mb-1">Hour (24hr)</label>
                  <select
                    className="form-select form-select-lg bg-light border-0"
                    style={{ fontSize: '1rem' }}
                    value={hour}
                    onChange={(e) => setHour(parseInt(e.target.value))}
                  >
                    {hours.map(h => (
                      <option key={h} value={h}>{h.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-fill">
                  <label className="text-muted small mb-1">Minute</label>
                  <select
                    className="form-select form-select-lg bg-light border-0"
                    style={{ fontSize: '1rem' }}
                    value={minute}
                    onChange={(e) => setMinute(parseInt(e.target.value))}
                  >
                    {minutesSeconds.map(m => (
                      <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-fill">
                  <label className="text-muted small mb-1">Second</label>
                  <select
                    className="form-select form-select-lg bg-light border-0"
                    style={{ fontSize: '1rem' }}
                    value={second}
                    onChange={(e) => setSecond(parseInt(e.target.value))}
                  >
                    {minutesSeconds.map(s => (
                      <option key={s} value={s}>{s.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-2">Date Range:</label>
              <div className="d-flex flex-wrap gap-4">
                <div className="flex-fill">
                  <label className="text-muted small mb-1">Start Date</label>
                  <div className="w-100">
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => {
                        setStartDate(date);
                        setDateError("");
                      }}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      minDate={today}
                      className="form-control form-control-lg bg-light border-0 w-100"
                      style={{ fontSize: '1rem' }}
                      dateFormat="MM/dd/yyyy"
                      required
                    />
                  </div>
                </div>
                <div className="flex-fill">
                  <label className="text-muted small mb-1">End Date</label>
                  <div className="w-100">
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => {
                        setEndDate(date);
                        setDateError("");
                      }}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      className="form-control form-control-lg bg-light border-0 w-100"
                      style={{ fontSize: '1rem' }}
                      dateFormat="MM/dd/yyyy"
                      required
                    />
                  </div>
                </div>
              </div>
              {dateError && <div className="text-danger small mt-2 fw-medium">{dateError}</div>}
            </div>

            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-1">Message Title</label>
              <input
                type="text"
                className="form-control form-control-lg bg-light border-0"
                style={{ fontSize: '1rem' }}
                value={messageTitle}
                onChange={(e) => setMessageTitle(e.target.value)}
                required
              />
            </div>
            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-1">Action Button</label>
              <input
                type="text"
                className="form-control form-control-lg bg-light border-0"
                style={{ fontSize: '1rem' }}
                value={messageAction}
                onChange={(e) => setMessageAction(e.target.value)}
                required
              />
            </div>
            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-1">Target URL <span className="fw-normal text-muted">(e.g., https://example.com - optional)</span></label>
              <input
                type="text"
                className="form-control form-control-lg bg-light border-0"
                style={{ fontSize: '1rem' }}
                value={targetUrl}
                onChange={(e) => setTargetUrl(e.target.value)}
                placeholder="Leave blank to use uploaded Detail Page below"
              />
            </div>

            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-1">Repeat Interval (Days) <span className="fw-normal text-muted">(Optional - e.g., 3)</span></label>
              <input
                type="number"
                min="1"
                className="form-control form-control-lg bg-light border-0"
                style={{ fontSize: '1rem' }}
                value={repeatIntervalDays}
                onChange={(e) => setRepeatIntervalDays(e.target.value)}
                placeholder="Leave blank for one-time message"
              />
            </div>

            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-1">Side Window Message Page <span className="text-danger">*</span></label>
              <input
                type="file"
                className="form-control form-control-lg bg-light border-0"
                style={{ fontSize: '1rem' }}
                onChange={handleMessageFileChange}
                ref={messageFileRef}
                required
              />
            </div>

            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-1">Normal Window Message Page <span className="fw-normal text-muted">(Optional)</span></label>
              <input
                type="file"
                className="form-control form-control-lg bg-light border-0"
                style={{ fontSize: '1rem' }}
                onChange={handleNormalMessageFileChange}
                ref={normalMessageFileRef}
              />
            </div>

            <div className="form-group mb-4">
              <label className="text-muted small fw-bold mb-1">Detail Page <span className="fw-normal">(Optional)</span></label>
              <input
                type="file"
                className="form-control form-control-lg bg-light border-0"
                style={{ fontSize: '1rem' }}
                onChange={handleDetailFileChange}
                ref={detailFileRef}
              />
            </div>

            <div className="form-group mt-5">
              <button className="btn-premium w-100 rounded-pill py-3" disabled={isLoading}>
                {isLoading ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <i className="bi bi-cloud-arrow-up-fill me-2"></i>
                )}
                <span className="fw-bold fs-6">Upload Message Page</span>
              </button>
            </div>
          </form>

          {message && (
            <div className={`alert ${successful ? 'alert-success' : 'alert-danger'} mt-4 border-0 shadow-sm rounded-3 d-flex align-items-center`} role="alert">
              <i className={`bi ${successful ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-2 fs-5`}></i>
              <div>{message}</div>
            </div>
          )}

          {(messageUrl || detailUrl || normalMessageUrl) && (
            <div className="mt-4 p-4 bg-light rounded-4 border-0">
              <h5 className="fw-bold text-dark mb-3"><i className="bi bi-file-earmark-check text-success me-2"></i>Uploaded Files</h5>
              {messageUrl && (
                <div className="mb-2 d-flex align-items-center">
                  <span className="badge bg-primary rounded-pill me-2">Side Message</span>
                  <a href={messageUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none fw-medium text-break">
                    {messageUrl}
                  </a>
                </div>
              )}
              {normalMessageUrl && (
                <div className="d-flex align-items-center mt-3">
                  <span className="badge bg-info rounded-pill me-2">Normal Message</span>
                  <a href={normalMessageUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none fw-medium text-break">
                    {normalMessageUrl}
                  </a>
                </div>
              )}
              {detailUrl && (
                <div className="d-flex align-items-center mt-3">
                  <span className="badge bg-secondary rounded-pill me-2">Detail</span>
                  <a href={detailUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none fw-medium text-break">
                    {detailUrl}
                  </a>
                </div>
              )}
{/*
              <button
                className="btn btn-outline-primary mt-2"
                onClick={() => openWindowsSideBySide(messageUrl, detailUrl)}
              >
                Reopen Windows Side by Side
              </button>
*/}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadMessagePage;
